import { Badge, Group, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import type { RecipientWindow, RecipientWindowMeta } from '@repletoryreach/sdk-api/inbox';
import { useT, type Translate } from '../../../../i18n/I18nProvider';

export interface WindowStatesProps {
  rows: RecipientWindow[];
  meta: RecipientWindowMeta | null;
  loading: boolean;
  /**
   * True once the message HAS to be a template, which is what turns a closed
   * row from a problem into a plain fact.
   */
  templateMode: boolean;
  /**
   * True once a template has actually been picked. Only then does the
   * explanation stop being useful: gating it on `templateMode` instead would
   * hide the sentence in exactly the case that needs it, because that mode is
   * itself the consequence of somebody being outside the window.
   */
  templateChosen: boolean;
}

/**
 * Where each chosen person stands against WhatsApp's 24-hour rule, said per
 * person and in plain words.
 *
 * This is the whole point of the screen. Meta only accepts a free-form message
 * within 24 hours of somebody writing to the business, so "you can type freely
 * to these two and must send a template to that one" has to be visible before
 * anything is written, not discovered afterwards from a failed send.
 *
 * The reading is the server's (`POST /v1/conversations/window`), which is the
 * same one the thread header has always shown: the newest inbound message on
 * the conversation, plus 24 hours.
 */
export function WindowStates({ rows, meta, loading, templateMode, templateChosen }: WindowStatesProps) {
  const t = useT();

  if (loading && rows.length === 0) {
    return (
      <Stack gap={6}>
        <Skeleton height={38} radius="sm" />
        <Skeleton height={38} radius="sm" />
      </Stack>
    );
  }
  if (rows.length === 0) return null;

  const mixed = meta !== null && meta.open > 0 && meta.closed > 0;

  return (
    <Stack gap="sm">
      {/* One sentence about the selection as a whole, before the list of
          people, because the decision it forces is about the message. */}
      {!templateChosen && meta && meta.closed > 0 && (
        <Alert color={mixed ? 'warning' : 'info'} title={t(mixed ? 'compose.window.mixed.title' : 'compose.window.closed.title')}>
          {mixed
            ? t('compose.window.mixed.body', { closed: meta.closed, total: meta.count })
            : t('compose.window.closed.body')}
        </Alert>
      )}

      <Stack gap={2} className="rr-compose__states">
        {rows.map((r) => (
          <div key={r.msisdn} className="rr-compose__state">
            <div className="rr-compose__state-who">
              <Text role="bodySmall" fw={600}>{r.contact_name ?? r.msisdn}</Text>
              {r.contact_name && (
                <Text role="caption" c="var(--rr-color-text-muted)">{r.msisdn}</Text>
              )}
            </div>
            <Group gap="xs" align="center" wrap="nowrap">
              <Text role="caption" c="var(--rr-color-text-muted)" className="rr-compose__state-why">
                {why(r, t)}
              </Text>
              {badge(r, templateMode, t)}
            </Group>
          </div>
        ))}
      </Stack>
    </Stack>
  );
}

/** Why this person is or is not reachable with plain text, in their own row. */
function why(r: RecipientWindow, t: Translate): string {
  if (!r.opted_in) return t('compose.window.why.optedOut');
  if (r.window_open) {
    const left = r.window_expires_at ? new Date(r.window_expires_at).getTime() - Date.now() : 0;
    const hours = Math.floor(left / 3_600_000);
    return hours >= 1
      ? t('compose.window.why.openHours', { hours })
      : t('compose.window.why.openMinutes', { minutes: Math.max(1, Math.floor(left / 60_000)) });
  }
  if (r.reason === 'never') return t('compose.window.why.never');

  // "last heard from them 3 days ago": the number that explains the refusal.
  const since = r.last_inbound_at ? Date.now() - new Date(r.last_inbound_at).getTime() : 0;
  const days = Math.floor(since / 86_400_000);
  return days >= 1
    ? t('compose.window.why.expiredDays', { days })
    : t('compose.window.why.expiredHours', { hours: Math.max(1, Math.floor(since / 3_600_000)) });
}

function badge(r: RecipientWindow, templateMode: boolean, t: Translate) {
  if (!r.opted_in) {
    return <Badge size="xs" color="red" variant="light">{t('compose.window.badge.optedOut')}</Badge>;
  }
  if (r.window_open) {
    return <Badge size="xs" color="green" variant="light">{t('compose.window.badge.open')}</Badge>;
  }
  // Outside the window a template still reaches them, so once one is chosen
  // the row stops reading as a problem.
  return templateMode
    ? <Badge size="xs" color="gray" variant="light">{t('compose.window.badge.templateOnly')}</Badge>
    : <Badge size="xs" color="orange" variant="light">{t('compose.window.badge.needsTemplate')}</Badge>;
}
